import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Inject,
  Injector,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { OrderformFascade } from '../../../../fascades/orderform.fascade';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { map } from 'rxjs/operators';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AbstractMethodComponent } from '../abstract-method.component';
import { InterfacePaymentProviderStipe, OrderformOrderState, OrderFormValidationResult } from '@orderform/widgets';

import { HttpClient } from '@angular/common/http';
import { CREDIT_CARD_DEFAULT_STYLES, CreditCardStyles } from '../../payment-methods.model';
import { ExternalResourcesLoaderService } from '../../../../services/external-resources-loader.service';
import { DOCUMENT } from '@angular/common';

interface StripeElement {
  addEventListener: (
    type: string,
    listener: (event: { complete?: boolean; error?: { message: string } }) => void
  ) => void;

  mount(selector: string): void;
}

interface StripeElementsCreator {
  create(type: string, options: any): StripeElement;
}

interface StripeInstance {
  elements(): StripeElementsCreator;

  createPaymentMethod(data: any): Promise<{
    paymentMethod?: { id: string };
    error?: { message: string };
  }>;
}

@Component({
  selector: 'orderform-payment-method-stripe-sepa-v2',
  templateUrl: './stripe-sepa-v2.component.html',
  styleUrls: ['./stripe-sepa-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StripeSepaV2Component),
      multi: true,
    },
  ],
})
export class StripeSepaV2Component
  extends AbstractMethodComponent
  implements OnInit, OnDestroy, ControlValueAccessor, AfterViewInit
{
  @Input() config: InterfacePaymentProviderStipe;
  @Input() styles?: CreditCardStyles = CREDIT_CARD_DEFAULT_STYLES;

  public stripeError$ = {
    iBan: new BehaviorSubject<string>(''),
  };
  private _stripeComplete = {
    iBan: false,
  };
  private readonly _subscriptionBeforeSubmit = this._orderformFascade.beforeSubmit.subscribe(() =>
    this._onBeforeSubmit()
  );
  private readonly _subscriptionFormValidation = this._orderformFascade.formValidation.subscribe(
    () => this._onFormValidation(),
    this._element
  );
  private _stripeInstance: StripeInstance;
  private _stripeIban: StripeElement;
  private _stripeToken: string;
  private _waitStripeInitialized: Promise<boolean>;

  private readonly document = this._injector.get(DOCUMENT);

  private missingAddressErrorParam: string = 'billing_details[address][line1]';

  constructor(
    private readonly _orderformFascade: OrderformFascade,
    @Inject(LOCALE_ID) private readonly _locale: string,
    injector: Injector,
    private readonly http: HttpClient,
    private readonly externalResourcesLoaderService: ExternalResourcesLoaderService
  ) {
    super(injector);
  }

  getStripe(): (publicKey: string, config: { locale: string }) => StripeInstance {
    return (<any>window).Stripe;
  }

  ngOnInit(): void {
    this._waitStripeInitialized = (async () => {
      if (!this.getStripe()) {
        await this.externalResourcesLoaderService.load(this.document, [this.config.apiUrl], [], true);
      }

      /**
       * get Stripe Function from asset load (_loadExternalResources)
       */
      const stripeFunction = this.getStripe();

      /**
       * Check stripe has loaded right
       */
      if (!stripeFunction) {
        console.log('Stripe does not exist');
        return false;
      }

      /**
       * create instance
       */
      this._stripeInstance = stripeFunction(this.config.publicKey, {
        locale: this._locale.replace(/-[a-zA-Z0-9]{2,3}$/, ''),
      });

      const elements = this._stripeInstance.elements();

      const style = {
        base: {
          iconColor: this.styles.textColor,
          color: this.styles.textColor,
          fontSize: parseFloat(this.styles.fontSize) * 16 + 'px', // send as px; 1rem = 16px
          ':-webkit-autofill': {
            color: this.styles.placeholderColor,
          },
          '::placeholder': {
            color: this.styles.placeholderColor,
          },
        },
        invalid: {
          iconColor: this.styles.textColor,
          color: this.styles.textColor,
        },
      };

      const classes = {
        complete: 'ng-valid',
        invalid: 'ng-invalid',
      };

      this._stripeIban = elements.create('iban', {
        placeholderCountry: 'DE',
        supportedCountries: ['SEPA'],
        style,
        classes,
      });

      this.addChangeListener();

      return true;
    })();
  }

  private addChangeListener() {
    const fields = {
      iBan: this._stripeIban,
    };
    Object.keys(fields).forEach((field) => {
      fields[field].addEventListener('change', (event) => this.onFieldChanged(field, event));
    });
  }

  private onFieldChanged(field, event) {
    this._stripeComplete[field] = event.complete;
    this._paymentDataChanged.next({ valid: this._isValid() });
    if (event.error) {
      this.stripeError$[field].next(event.error.message);
    } else {
      this.stripeError$[field].next('');
    }
  }

  public async ngAfterViewInit() {
    if ((await this._waitStripeInitialized) === false) {
      return;
    }

    this._stripeIban.mount('#stripe-iban-' + this.uniqueInstanceId);
  }

  async createStripeIntent(order: OrderformOrderState): Promise<any> {
    const { protocol, hostname, port, href } = window.location;
    let baseUrl = `${protocol}//${hostname}`;
    if (port) {
      baseUrl += ':' + port;
    }
    const apiUrl = baseUrl + '/buy/stripe_v2/create_intent';
    const postData = {
      currency: order.order.currency,
      amount: order.order_summary?.order_final?.gross_1st ?? 0,
      payment_plan_id: order.order.payment_plan_id,
      payment_provider_id: order.order.payment_provider?.payment_provider_id,
      url: href,
      purchase_id: order.order.purchase_id,
      email: order.order.shipping_address.buyer_address_email ?? '',
    };

    try {
      return firstValueFrom(this.http.post(apiUrl, postData));
    } catch (error) {
      this._monitoring.trackValidationError(
        `stripe_intent_creation_failed:${error?.message || 'unknown'}`,
        'frontend',
        'stripe-sepa-v2'
      );
      throw error;
    }
  }
  async getOrder() {
    return firstValueFrom(this._orderformFascade.order$.pipe(map((order) => order)));
  }

  createPaymentMethod(order: any) {
    return {
      type: 'sepa_debit',
      sepa_debit: this._stripeIban,
      billing_details: {
        name:
          order.order.shipping_address.buyer_address_first_name +
          ' ' +
          order.order.shipping_address.buyer_address_last_name,
        email: order.order.shipping_address.buyer_address_email,
        address: {
          city: order.order.shipping_address.buyer_address_city,
          country: order.order.shipping_address.buyer_address_country,
          line1:
            order.order.shipping_address.buyer_address_street +
            ' ' +
            order.order.shipping_address.buyer_address_street_number,
          line2: order.order.shipping_address.buyer_address_street2,
          postal_code: order.order.shipping_address.buyer_address_zipcode,
        },
      },
    };
  }

  private async _onBeforeSubmit(): Promise<{ valid: boolean }> {
    if (!this.isActive()) {
      return Promise.resolve({ valid: true });
    }
    if (this._stripeToken) {
      this._paymentDataChanged.next({
        valid: true,
        stripe_token: this._stripeToken,
      });
      return Promise.resolve({
        valid: true,
      });
    }

    if (!this._isValid()) {
      return Promise.resolve({ valid: false });
    }

    const order = await this.getOrder();
    try {
      const paymentMethod: { sepa_debit: any; billing_details?: any } = this.createPaymentMethod(order);
      const stripePaymentMethod = await this._stripeInstance.createPaymentMethod(paymentMethod);

      if (stripePaymentMethod.error) {
        return this.handleStripeError(stripePaymentMethod);
      }
      const intentResponse = await this.createStripeIntent(order);
      if (intentResponse && intentResponse.type) {
        const intentId = this.extractIntentId(intentResponse.intentClientSecret);
        this._stripeToken = intentId;
        this._paymentDataChanged.next({
          valid: true,
          stripe_token: this._stripeToken,
          payment_method_id: stripePaymentMethod.paymentMethod.id,
          user_agent: navigator.userAgent,
        });
        return { valid: true };
      }
    } catch (error) {
      return this.handleStripeError(error);
    }

    return { valid: false };
  }

  private _isValid() {
    const notCompletedFields = Object.keys(this._stripeComplete).filter((field) => !this._stripeComplete[field]);
    const notCompleted = notCompletedFields.length > 0;
    return !notCompleted;
  }

  ngOnDestroy() {
    this._subscriptionBeforeSubmit.unsubscribe();
    this._subscriptionFormValidation.unsubscribe();
  }

  private _onFormValidation(): Promise<OrderFormValidationResult> {
    if (!this.isActive() || this._isValid()) {
      return Promise.resolve({
        valid: true,
        component: 'ds-orderform-payment-stripe',
        invalidFieldList: [],
      });
    }
    const notCompletedFields = Object.keys(this._stripeComplete).filter((field) => !this._stripeComplete[field]);
    // show generic error
    const invalidFieldList: { id: string; errors: string[] }[] = [];
    notCompletedFields.forEach((field) => {
      if (this.stripeError$[field].getValue() === '') {
        this.stripeError$[field].next(
          $localize`:@@payment_method.label.stripe_incomplete_generic:Die Eingabe wird benötigt.`
        );
        invalidFieldList.push({ id: field, errors: ['required'] });
      } else {
        invalidFieldList.push({
          id: field,
          errors: [this.stripeError$[field].getValue()],
        });
      }
    });
    return Promise.resolve({
      valid: false,
      component: 'ds-orderform-payment-stripe',
      invalidFieldList,
    });
  }

  private async handleStripeError(result) {
    if (result.error?.param === this.missingAddressErrorParam) {
      this._toaster.error(
        $localize`:@@payment_method.elv.iban_country_error:Bitte geben Sie eine IBAN aus dem Land Ihrer Anschrift an. Alternativ wählen Sie eine andere Zahlungsmöglichkeit.`
      );
      this._monitoring.trackValidationError(
        `exception: Stripe Sepa: Address field is required`,
        'frontend',
        'ds-orderform-payment-stripe-sepa-v2'
      );
      return { valid: false };
    }
    this._toaster.error(
      result.error?.message ??
        $localize`:@@payment_method.error.generic:Die Zahlung konnte jetzt nicht verarbeitet werden. Bitte probieren es mit einer anderen Bezahlweise nochmal.`
    );
    const errorCode = result.error?.code || 'unknown';
    const errorDetails = result.error ? JSON.stringify(result.error) : '{}';

    this._monitoring.trackValidationError(
      `exception:${errorCode}:${errorDetails}`,
      'frontend',
      'ds-orderform-payment-stripe-sepa-v2'
    );
    return { valid: false };
  }

  /**
   * Extract intent ID from client secret
   * Format: pi_1234567890_secret_abcdef -> pi_1234567890
   * Format: seti_1234567890_secret_abcdef -> seti_1234567890
   */
  private extractIntentId(clientSecret: string): string {
    if (!clientSecret) return '';

    // Split by '_secret_' and take the first part
    const parts = clientSecret.split('_secret_');
    return parts[0] || '';
  }
}
